import { Trans, useTranslation } from "react-i18next";
import { Picture } from "~/components/ui/picture";
import { LINE_BREAKS } from "~/components/ui/max-width-line-break";
import { cn } from "~/lib/utils";
import { imageUrl } from "~/lib/image-url";

interface DeepDive {
    id: "visibility" | "operations";
    // Plain public/ paths so Picture can serve the AVIF/WebP siblings: flattened composites of
    // the design's product art, glow and overlays. `mobileImage` is served below lg.
    image: string;
    mobileImage?: string;
    // Panel layout: the image frame's width per breakpoint, and how the image fills it.
    imageClassName: string;
    imgClassName: string;
    copyClassName: string;
    // The design's panel height (mobile and lg), so section spacing matches even when text
    // or the image frame rounds a pixel short.
    panelClassName: string;
    imageFirst: boolean;
}

const PANELS: DeepDive[] = [
    {
        id: "visibility",
        image: imageUrl("/images/fleet-dark/increase-fleet-visibility-dark.png"),
        // The PNG's built-in glow is wider than the design's frame, so the art is scaled past
        // the frame (glow spilling out, as in the design) to land at ~70% of its width.
        imageClassName: "relative aspect-[630/418] max-w-[331px] md:max-w-[500px] lg:w-[53%] lg:max-w-[630px]",
        imgClassName: "absolute top-[calc(50%-1rem)] left-1/2 w-[139%] max-w-none lg:w-[142%] -translate-x-1/2 -translate-y-1/2",
        copyClassName: "lg:flex-1",
        panelClassName: "min-h-[380px] lg:min-h-[418px]",
        imageFirst: false,
    },
    {
        id: "operations",
        image: imageUrl("/images/fleet/van-stats.png"),
        mobileImage: imageUrl("/images/fleet/van-stats-mobile.png"),
        imageClassName: "max-w-[328px] md:max-w-[460px] lg:w-[48%] lg:max-w-[569px]",
        imgClassName: "h-auto w-full",
        copyClassName: "lg:max-w-[503px] lg:flex-1",
        panelClassName: "lg:min-h-[543px]",
        imageFirst: true,
    },
];

function DeepDivePanel({ panel }: { panel: DeepDive }) {
    const { t } = useTranslation("fleet");
    const titleId = `fleet-deep-dive-${panel.id}`;

    return (
        <section
            aria-labelledby={titleId}
            className={cn(
                "flex flex-col gap-6 lg:items-center lg:justify-between lg:gap-12 xl:gap-16",
                panel.imageFirst ? "lg:flex-row-reverse" : "lg:flex-row",
                panel.panelClassName,
            )}
        >
            {/* Above the image so its spilled glow never tints the copy. */}
            <div className={cn("relative z-10 flex flex-col gap-2 lg:gap-4", panel.copyClassName)}>
                <h2
                    id={titleId}
                    className="font-display text-text-heading text-xl leading-[1.15] font-extrabold lg:max-w-[464px] lg:text-4xl lg:leading-[1.1]"
                >
                    <Trans t={t} i18nKey={`deep_dives.${panel.id}.title`} components={LINE_BREAKS} />
                </h2>
                <p className="text-text-body-secondary lg:text-md text-sm leading-5 lg:leading-[1.1]">
                    {t(`deep_dives.${panel.id}.body`)}
                </p>
            </div>
            <div className={cn("mx-auto w-full shrink-0 lg:mx-0", panel.imageClassName)}>
                <Picture
                    src={panel.image}
                    mobileSrc={panel.mobileImage}
                    alt={t(`deep_dives.${panel.id}.image_alt`)}
                    loading="lazy"
                    className={panel.imgClassName}
                />
            </div>
        </section>
    );
}

// Two alternating copy/image panels. Must render inside the .fleet token scope.
export function FleetFeatureDeepDives() {
    return (
        <div className="wrap flex flex-col gap-16 py-8 lg:gap-[72px] lg:py-[60px]">
            {PANELS.map((panel) => (
                <DeepDivePanel key={panel.id} panel={panel} />
            ))}
        </div>
    );
}

export default FleetFeatureDeepDives;
